/*
 *   File : add-bank.js
 *   Author : https://evoqins.com
 *   Description : Onboarding step  - Add Bank page
 *   Integrations : react-toastify
 *   Version : 1.0.0
 *   Date : 14-10-2025  
*/

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Modal } from 'bootstrap';


// *********** Components ***************//  
import { Icon } from "../../../Component/Icon";
import { PrimaryButton, TextButton } from "../../../Component/Buttons";
import { CustomTextInput, FilePicker, RadioGroup } from "../../../Component/Form-Elements";
import { FindIFSCModal, SearchBankModal, SearchBranchModal, VerifyBankModal } from "../../../Component/Modal";
import { APIService, awsService } from '../../../Service';
import { _checkFileSize, _getSignupProgress } from "../../../Helper";
import { OverLay } from "../../../Component/Other";

// *********** Styles ***************//  
import styles from "../../../Style/Component/login.module.scss";
import Colors from "../../../Style/color.scss";

// *********** Assets ***************//  
import Loader from "../../../Assets/Images/loader.gif";
import BankVerifyLoader from "../../../Assets/Images/KYC/bank-verify-loader.gif";


const RETRY_LIMIT = 120; // 120 times 10mins - each 5s delay

function AddBank(props) {

    //get general data from redux
    const GENERAL_DATA = useSelector((store) => store.Reducer.GENERAL_DATA);
    const [generalData, setGeneralData] = useState(GENERAL_DATA);
    //get progress data form redux
    const PROGRESS_DATA = useSelector((store) => store.Reducer.SIGNUP_PROGRESS_DATA);

    const [accountNumber, setAccountNumber] = useState("");
    const [accountNumberError, setAccountNumberError] = useState("");
    const [holderName, setHolderName] = useState("");
    const [holderNameError, setHolderNameError] = useState("");
    const [ifsc, setIfsc] = useState("");
    const [ifscError, setIfscError] = useState("");
    const [accountType, setAccountType] = useState("");

    const [ifscModal, setIfscModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [showAddBank, setShowAddBank] = useState(false);

    const [bankSearchModal, setBankSearchModal] = useState(false);
    const [selectedBank, setSelectedBank] = useState({});

    const [branchSearchModal, setBranchSearchModal] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState({});
    const [bankList, setBankList] = useState([]);
    const [bankDataSearch, setBankDataSearch] = useState("");
    const [bankDataLoading, setBankDataLoading] = useState(true);
    const [branchList, setBranchList] = useState([]);
    const [branchSearch, setBranchSearch] = useState("");
    const [branchDataLoading, setBranchDataLoading] = useState(true);
    const [availableBanks, setAvailableBanks] = useState([]);
    const [showAddBankProofModal, setShowAddBankProofModal] = useState(false); // proof upload modal if verification is required
    const [bankVerificationStatus, setBankVerificationStatus] = useState(""); // bank verification status

    const [bankProofFile, setBankProofFile] = useState(null);
    const [bankProofFileError, setBankProofFileError] = useState("");
    const [bankProofViewUrl, setBankProofViewUrl] = useState(null);
    const [bankProofLoading, setBankProofLoading] = useState(false);
    const [showVerifyLoader, setShowVerifyLoader] = useState(false);

    useEffect(() => {

        if (GENERAL_DATA) {
            setGeneralData(GENERAL_DATA);
        }
    }, [GENERAL_DATA]);

    useEffect(() => {

        if (PROGRESS_DATA) {
            const bankDetails = PROGRESS_DATA?.kyc_data?.bank_details;
            if (Array.isArray(bankDetails) && bankDetails.length > 0) {
                setAvailableBanks(bankDetails);
                setDataLoading(false);
            } else if (bankDetails && typeof bankDetails === "object" && Object.keys(bankDetails).length > 0) {
                // single bank object — wrap into array for consistency with UI mapping
                setAvailableBanks([bankDetails]);
                setDataLoading(false);
            } else {
                _getAvailableBank();
            }
        }
    }, [PROGRESS_DATA]);

    useEffect(() => {
        if (generalData.bank_account_types) {
            setAccountType(props.isNRI ? generalData?.bank_account_types[0]?.id : generalData?.bank_account_types[2]?.id);
        }
    }, [generalData]);

    useEffect(() => {
        if (ifscModal === true) {
            let ifsc_modal = new Modal(document.getElementById('find-ifsc-modal'));
            ifsc_modal.show();
        };
    }, [ifscModal]);

    useEffect(() => {
        if (bankSearchModal === true) {
            let bank_modal = new Modal(document.getElementById('bank-search-modal'));
            bank_modal.show();
        };
    }, [bankSearchModal]);

    useEffect(() => {
        if (branchSearchModal === true) {
            let branch_modal = new Modal(document.getElementById('branch-search-modal'));
            branch_modal.show();
        };
    }, [branchSearchModal]);

    useEffect(() => {

        if (showAddBankProofModal) {
            let bank_modal = new Modal(document.getElementById('verify-bank-modal'));
            bank_modal.show();
        }

    }, [showAddBankProofModal]);

    useEffect(() => {
        if (bankSearchModal === true) {
            _getBank();
            setBankDataLoading(true);
        }
    }, [bankDataSearch, bankSearchModal]);

    useEffect(() => {
        if (branchSearchModal === true) {
            if (branchSearch.length > 2 || branchSearch.length === 0) {
                _getBranch();
                setBranchDataLoading(true);
            }
        }
    }, [branchSearch, branchSearchModal]);

    useEffect(() => {
        setAccountNumberError("");
    }, [accountNumber]);

    useEffect(() => {
        setHolderNameError("");
    }, [holderName]);

    useEffect(() => {
        setIfscError("");
    }, [ifsc]);



    // Back to previous step -Onboarding
    const _handleBackToPrevious = () => {
        if (props.kycUser === false && PROGRESS_DATA.is_identity_verified === true) {
            props.handleBack(2); // Aadhaar fetch already done
        } else {
            props.handleBack(props.kycUser ? 2 : 3); // 2 - Edit Personal Details, 3 - Aadhaar Verification      
        }
    };

    // Continue to next step - Onboarding
    const _handleContinue = () => {

        if (availableBanks.length === 0) {
            toast.dismiss();
            toast.info("Please add bank details to continue", {
                className: "e-toast",
            });
            // setShowAddBank(true);
        } else {
            let default_bank = availableBanks.find(bank => bank.is_default === true);
            let type = 2;
            setLoading(true);
            // API call
            _addBank(default_bank, type);
        }
    };

    // Add more bank
    const _handleAddMoreBank = () => {
        setShowAddBank(true);
    };


    // handle account number
    const _handleChangeAccountNumber = (value) => {
        if (value.length <= 18) {
            setAccountNumber(value);
        }
    };

    // handle account holder name
    const _handleAccountHolderNameChange = (value) => {
        setHolderName(value);
    };

    //handle account type
    const _handleAccountType = (id) => {
        setAccountType(id)
    };

    // handle ifsc code
    const _handleChangeIfsc = (value) => {
        if (value.length <= 11) {
            setIfsc(value.toUpperCase());
        }
    };

    // handle open find ifsc modal 
    const _handleIfscModal = () => {
        setIfscModal(true);
    };

    // handle open bank search modal 
    const _handleBankSearchModal = () => {
        setBankSearchModal(true);
    };

    // handle select bank 
    const _handleSelectBank = (selected) => {
        setSelectedBank(selected);
    };

    // handle open branch search modal 
    const _handleBranchSearchModal = () => {
        setBranchSearchModal(true);
    };

    // handle select branch 
    const _handleSelectBranch = (selected) => {
        setSelectedBranch(selected);
    };

    // handle ifsc 
    const _handleFind = (data) => {
        setIfsc(data.ifsc);
    };


    //handle submit add bank
    const _handleSubmitAddBank = () => {

        let errors = [];

        if (accountNumber.length === 0) {
            setAccountNumberError("Provide your bank account number");
            errors.push("account-number");
        }

        if (holderName.length === 0) {
            setHolderNameError("Provide your full name");
            errors.push("account-holder");
        }

        if (ifsc.length === 0) {
            setIfscError("Please provide IFSC code");
            // setIfscError("Please provide your bank's IFSC for secure bank transactions");
            errors.push("ifsc-code");
        }

        if (props.isNRI && bankProofFile === null) {
            setBankProofFileError("Please upload your bank proof");
            errors.push("bank-proof");
        }


        if (errors.length === 0) {
            setLoading(true);
            _addBank();
        }


        // Scrolling to first error
        if (errors.length !== 0) {
            let first_err_id = errors[0];
            let element = document.getElementById(first_err_id);
            element.scrollIntoView({ behavior: "smooth" });
        };

    };

    const _handleSetAsDefaultBank = (bank_index) => {
        let updated_bank_list = availableBanks.map((bank, index) => {
            if (index === bank_index) {
                return {
                    ...bank,
                    "is_default": !bank.is_default
                }
            } else {
                return {
                    ...bank,
                    "is_default": false
                }
            }
        });

        setAvailableBanks(updated_bank_list);

    };

    const _handleBankProofAddSuccess = () => {
        let default_bank = availableBanks.find(bank => bank.is_default === true);
        let bank_data = accountNumber.length === 0 ? default_bank : {
            "account_number": accountNumber,
            "account_holder_name": holderName,
            "account_type": accountType,
            "ifsc": ifsc,
            "is_default": true
        };
        setLoading(true);
        _addBank(bank_data, 2); // 2 - kyc user bank proof add
    };

    const _handleUploadBankProof = (file) => {

        if (file && file?.target?.files?.length !== 0) {
            const is_file_size_valid = _checkFileSize(file.target.files[0].size);

            if (is_file_size_valid === false) {
                setBankProofFileError("Maximum file size should be 5mb only");
                return;
            }
            _getPresignedUrl(file);
        } else {
            setBankProofFile(null);
        }
    };


    // ================================ //
    //============= API CALLS ========= //

    const _getAvailableBank = async () => {
        const is_post = false;
        let url = 'kyc/list-bank';
        let api_version = 3;
        let token = sessionStorage.getItem('access_token');
        let data = {};
        let response = await APIService(is_post, url, data, token, api_version)
        if (response.status_code === 200) {
            setAvailableBanks([response.data]);
            setDataLoading(false);

            setLoading(true);
            toast.dismiss();
            toast.info("Bank verification under progress", {
                className: "e-toast",
            });

            // Bank verification
            await _verifyBank();
        } else {
            setDataLoading(false);
        }
    }

    //API- Get bank list
    const _getBank = () => {
        let is_post = true;
        let url = 'general/banks';
        let data = {
            "query": bankDataSearch.length !== 0 ? bankDataSearch : null
        };
        let token = sessionStorage.getItem('access_token');


        APIService(is_post, url, data, token).then((response) => {
            if (response.status_code === 200) {
                setBankList(response.data);
            } else {
                setBankList([]);
            }
            setBankDataLoading(false);
        });
    };

    //API- Get branch list
    const _getBranch = () => {
        let is_post = true;
        let url = 'general/branches';
        let data = {
            "bank_id": selectedBank.id,
            "query": branchSearch.length !== 0 ? branchSearch : null
        };
        let token = sessionStorage.getItem('access_token');

        APIService(is_post, url, data, token).then((response) => {
            if (response.status_code === 200) {
                setBranchList(response.data);
            } else {
                setBranchList([]);
            }
            setBranchDataLoading(false);
        });
    };

    //API- Add bank
    const _addBank = async (bank_data, type = 1) => {


        let api_version = 3;
        let is_post = true;
        let url = 'kyc/save-bank';
        let data = {
            "account_number": type === 2 ? bank_data.account_number : accountNumber,
            "account_holder_name": type === 2 ? bank_data.account_holder_name : holderName,
            "account_type": type === 2 ? (bank_data.account_type_id || bank_data.account_type) : accountType,
            "ifsc": type === 2 ? bank_data.ifsc : ifsc,
            "is_default": type === 2 ? bank_data.is_default : true,
            "bank_proof_url": props.isNRI ? bankProofViewUrl : null,
        };
        let token = sessionStorage.getItem('access_token');

        let response = await APIService(is_post, url, data, token, api_version)
        if (response.status_code === 200) {
            if (type === 2) {
                toast.success("Bank details updated successfully", {
                    className: "e-toast",
                });
                // Add Nominee details step       
                props.handleUpdateCurrentStep(props.kycUser ? 4 : 5);

            } else {
                if (props.isNRI) {
                    setShowAddBank(false);
                    setLoading(false);
                    toast.dismiss();
                    toast.success("Bank added successfully", {
                        className: "e-toast",
                    });
                    // Updatting data
                    _getSignupProgress();
                } else {
                    // Verify bank verification status
                    await _verifyBank();
                }

            }
        } else {
            toast.dismiss();
            toast.error(response.message, {
                type: "error",
                className: "e-toast",
            });
            setLoading(false);
        }

    };

    //API- Add bank
    const _verifyBank = async (retry = RETRY_LIMIT) => {


        let api_version = 3;
        let is_post = false;
        let url = 'kyc/check-bank-verification';
        let data = null;
        let token = sessionStorage.getItem('access_token');

        let response = await APIService(is_post, url, data, token, api_version)
        if (response.status_code === 200) {

            if (response.data.status === "SUCCESS") {
                setShowAddBank(false);
                setLoading(false);
                toast.dismiss();
                toast.success(response.data.message, {
                    className: "e-toast",
                });

                // Updatting data
                _getSignupProgress();
                setShowVerifyLoader(false);
                return;
            } else if (response.data.status === "FAILED") {

                toast.dismiss();
                toast.error(response.data.message, {
                    className: "e-toast",
                });


                if (response.data.proof_required === true) {
                    setShowAddBankProofModal(true);
                } else {
                    setShowAddBank(false);
                    setLoading(false);
                    // Updatting data
                    _getSignupProgress();
                }

                setShowVerifyLoader(false);
                return;
            } else {
                // Call again till get "success"
                await new Promise((resolve) => setTimeout(resolve, 5000));
                let re_try_limit = retry ?? RETRY_LIMIT; // totally 10 times

                if (!re_try_limit) {
                    toast.dismiss();
                    toast.error("Bank added successfully, but verification is pending", {
                        className: "e-toast",
                    });
                    setShowAddBank(false);
                    setLoading(false);
                    _getSignupProgress();
                    return;
                } else {
                    setShowVerifyLoader(true);
                }

                await _verifyBank(--re_try_limit);
            }
        } else {
            toast.dismiss();
            toast.error(response.message, {
                className: "e-toast",
            });
            setLoading(false);
            setShowVerifyLoader(false);
        }

    };

    //get presigned url
    async function _getPresignedUrl(file) {

        setBankProofLoading(true);

        let file_name = file.target.files[0].name;

        let is_post = true;
        let url = 'file/upload';
        let data = {
            "type": file.target.files[0].type,
            "file": file_name,
            "is_public": true
        };

        try {
            let response = await APIService(is_post, url, data);
            let aws_URL = '';
            if (response.status_code == 200) {

                aws_URL = response.data.upload_info.url;
                let view_url = response.data.view_info;

                let file_param = new File(
                    [file.target.files[0]],
                    file.target.files[0].name,
                    { type: file.target.files[0].type }
                );

                try {
                    let awsResponse = await awsService(response.data.upload_info.fields, file_param, aws_URL);

                    if (awsResponse.status == 'ok') {
                        setBankProofViewUrl(view_url);
                        setBankProofFile(file.target.files[0]);
                        setBankProofFileError("");
                    } else {
                        toast.dismiss();
                        toast.error(awsResponse.message, { className: "e-toast" });
                    }
                } catch (err) {
                    toast.dismiss();
                    toast.error(err.message, { className: "e-toast" });
                }
            }

            setBankProofLoading(false);

        } catch (err) {
            setBankProofLoading(false);
        }
    };


    const VerifyLoader = () => (
        <>
            <OverLay isOpen={true} onClose={() => { }} />
            <div className="position-fixed top-50 start-50 translate-middle e-bg-lotion e-border-radius-24 e-modal-box-shadow z-index-99">
                <div className="modal-gradient-bg d-flex flex-column align-items-center justify-content-between modal-body p-4 e-border-radius-24">
                    <img src={BankVerifyLoader}
                        className="object-fit-contain mt-4"
                        draggable={false}
                        alt="bank verification loader" />
                    <p className="e-font-14-res text-center e-text-charleston-green e-alt-font-poppins e-font-18 e-font-weight-600 mb-2">
                        We are verifying your bank details
                    </p>
                    <p className="e-font-12-res text-center e-text-dark-liver e-alt-font-poppins e-font-14 e-font-weight-400 line-height-26px mb-0">
                        Please keep the app open while this process completes.
                    </p>
                </div>
            </div>
        </>
    );


    return (
        <>
            <div className="col-sm-10 col-xl-9 mx-auto px-sm-0">
                <div className="e-padding-block-40px-res padding-76px-top padding-150px-bottom position-relative">
                    <div className={`${styles.e_onboarding_wrapper_personal_details} e-border-radius-24`}>
                        {
                            dataLoading ?
                                <div className="min-h-100vh text-center mt-5 pt-5" >
                                    <img src={Loader}
                                        className='object-fit-contain w-20 width-24px-res'
                                        draggable="false"
                                        alt="loader" />
                                </div> :
                                <>
                                    {
                                        showAddBank ?
                                            <>
                                                <div className="row flex-column flex-sm-row justify-content-sm-between align-items-sm-center row-gap-2  margin-40px-bottom">
                                                    <div className="col">
                                                        <div className="d-flex gap-3 gap-sm-4">
                                                            <Icon icon="arrow-back-left"
                                                                className="cursor-pointer"
                                                                size={24}
                                                                onClick={() => setShowAddBank(false)} />
                                                            <h1 className="e-text-chinese-black e-font-16-res e-font-20 e-alt-font-poppins e-font-weight-600 line-height-24px mb-0">
                                                                Add Bank
                                                            </h1>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row gx-gy-32px mb-4 row-cols-2">
                                                    <div className="col-sm-6 col-lg-4" id='account-number'>
                                                        <CustomTextInput label="Account number*"
                                                            placeholder="99980107428"
                                                            type="text"
                                                            style="number"
                                                            maxLength={18}
                                                            floatingLabel
                                                            value={accountNumber}
                                                            errorMsg={accountNumberError}
                                                            handleChange={_handleChangeAccountNumber} />
                                                    </div>
                                                    <div className="col-sm-6 col-lg-4" id="account-holder">
                                                        <CustomTextInput label="Account holder name*"
                                                            placeholder="Jhon Doe"
                                                            type="text"
                                                            floatingLabel
                                                            value={holderName}
                                                            errorMsg={holderNameError}
                                                            handleChange={_handleAccountHolderNameChange} />
                                                    </div>

                                                    {/* dummy div for alignment */}
                                                    {
                                                        !props.isNRI &&
                                                        <div className="col-sm-6 col-lg-4 d-none d-lg-block" id="account-holder">
                                                        </div>
                                                    }

                                                    <div className="col-sm-6 col-lg-4 position-relative" id="ifsc-code">
                                                        {/* IFSC code */}
                                                        <div className="position-relative">
                                                            <CustomTextInput label="IFSC code*"
                                                                placeholder="SBIN0001234"
                                                                type="text"
                                                                floatingLabel
                                                                value={ifsc}
                                                                errorMsg={ifscError}
                                                                handleChange={_handleChangeIfsc} />
                                                            {/* find ifsc  */}
                                                            <p className='mb-0 text-end'>
                                                                <span className='position-absolute top-100 end-0 pe-1 bg-white pt-1 e-text-egyptian-blue  e-font-12 e-alt-font-poppins e-font-weight-500 line-height-14px  cursor-pointer text-decoration-hover-underline'
                                                                    onClick={_handleIfscModal}>
                                                                    Find IFSC code
                                                                </span>
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {
                                                        props.isNRI &&
                                                        <div className="col-sm-6 col-lg-4">
                                                            {/* Upload bank proof card */}
                                                            <div id='bank-proof'>
                                                                <FilePicker id="bank-proof-image"
                                                                    label="Upload bank proof (max. 5mb)*"
                                                                    type="file"
                                                                    className="w-100"
                                                                    accept=".png, .jpg, .jpeg"
                                                                    isFileUploading={bankProofLoading ? true : false}
                                                                    file={bankProofFile}
                                                                    error={bankProofFileError}
                                                                    selectFile={_handleUploadBankProof} />
                                                            </div>
                                                        </div>
                                                    }


                                                    <div className={`col-sm-6 col-lg-auto`}
                                                        id='account-type'>
                                                        <h6 className='margin-12px-bottom e-text-charleston-green e-font-14 e-alt-font-poppins e-font-weight-400'>
                                                            Account type*
                                                        </h6>
                                                        <div className='d-flex flex-wrap gap-3'>
                                                            <RadioGroup data={generalData.bank_account_types}
                                                                selected={accountType}
                                                                handleSelect={_handleAccountType} />
                                                        </div>
                                                    </div>


                                                </div>

                                                <div className="row justify-content-end">
                                                    <div className="col-auto col-sm-6 col-md-4 col-lg-4 flex-fill-res ">
                                                        <PrimaryButton name='Add'
                                                            loading={loading}
                                                            disabled={loading}
                                                            className={`w-100 padding-12px-tb padding-8px-tb-res padding-12px-lr-res e-font-16`}
                                                            onPress={_handleSubmitAddBank} />
                                                    </div>
                                                </div>


                                            </>
                                            :
                                            <>


                                                <div className="row flex-column flex-sm-row justify-content-sm-between align-items-sm-center row-gap-2  margin-40px-bottom">
                                                    <div className="col">
                                                        <div className="d-flex gap-3 gap-sm-4">
                                                            <Icon icon="arrow-back-left"
                                                                className="cursor-pointer"
                                                                size={24}
                                                                onClick={_handleBackToPrevious} />
                                                            <h1 className="e-text-chinese-black e-font-16-res e-font-20 e-alt-font-poppins e-font-weight-600 line-height-24px mb-0">
                                                                Bank Details
                                                            </h1>
                                                        </div>
                                                    </div>

                                                    <div className="col-10 col-sm-6 col-lg-4 ">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="flex-fill progress h-6 e-bg-lavender e-border-radius-16" role="progressbar" aria-label="Success example" aria-valuenow="50" aria-valuemin="0" aria-valuemax="100">
                                                                <div className={`${props.kycUser ? "w-66per" : "w-60"} progress-bar progress-bar-bg e-border-radius-16`} ></div>
                                                            </div>
                                                            <p className='mb-0 e-text-egyptian-blue e-font-12 e-alt-font-poppins e-font-weight-500 line-height-24px'>
                                                                Step {props.kycUser ? 2 : 3} of {props.kycUser ? 3 : 5}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row flex-wrap g-4 margin-32px-bottom">

                                                    {
                                                        availableBanks.length ?
                                                            availableBanks.map((bank, key) => {
                                                                return (
                                                                    <div className="col-md-6 col-lg-6 col-xl-5 ">
                                                                        <div className="d-flex flex-column gap-2 p-3 e-bg-cultured e-border-radius-16 h-100">
                                                                            <div className="d-flex align-items-start  gap-12px mb-2">
                                                                                <p className="e-text-charleston-green    e-font-14-res e-font-16 e-alt-font-poppins e-font-weight-500 line-height-20px mb-0">
                                                                                    {bank?.bank_name}
                                                                                </p>
                                                                                {
                                                                                    bank?.is_default ?
                                                                                        <img src={require("../../../Assets/Images/Onboarding/tick-succes.svg").default}
                                                                                            className="ms-auto"
                                                                                            alt="Success tick"
                                                                                            width={24}
                                                                                            height={24}
                                                                                            draggable="false"
                                                                                            onClick={() => _handleSetAsDefaultBank(key)} />
                                                                                        :
                                                                                        <span className="ms-auto cursor-pointer">
                                                                                            <Icon icon="circle-inactive"
                                                                                                size={24}
                                                                                                onClick={() => _handleSetAsDefaultBank(key)} />
                                                                                        </span>
                                                                                }
                                                                            </div>
                                                                            <p className="d-flex gap-2-all align-items-center e-text-granite-gray  e-font-14 e-alt-font-poppins e-font-weight-400 line-height-16px mb-0">
                                                                                <Icon icon="bullet"
                                                                                    color={Colors.granite_gray}
                                                                                    size={"0.25rem"} />
                                                                                <Icon icon="bullet"
                                                                                    color={Colors.granite_gray}
                                                                                    size={"0.25rem"} />
                                                                                <Icon icon="bullet"
                                                                                    color={Colors.granite_gray}
                                                                                    size={"0.25rem"} />
                                                                                <Icon icon="bullet"
                                                                                    color={Colors.granite_gray}
                                                                                    size={"0.25rem"} />
                                                                                {bank?.account_number && bank?.account_number.slice(-4)}
                                                                            </p>
                                                                            <p className="d-flex gap-2-all align-items-center e-text-sonic-silver e-font-14 e-alt-font-poppins e-font-weight-400 line-height-16px mb-0">
                                                                                {bank?.account_holder_name}
                                                                                <Icon icon="bullet"
                                                                                    color={Colors.granite_gray}
                                                                                    size={"0.25rem"} />
                                                                                IFSC: {bank?.ifsc}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })
                                                            :
                                                            <div className="col-md-6 col-lg-7">
                                                                <p className=" e-bg-cornsilk e-border-radius-16 p-sm-3  p-2 e-text-ochre e-alt-font-poppins e-font-14 e-font-weight-400 line-height-24px" >
                                                                    Bank details not found, please add bank to continue
                                                                </p>
                                                            </div>
                                                    }
                                                </div>

                                                <div className="row justify-content-end align-items-center row-gap-3">
                                                    <div className="col-auto">
                                                        <TextButton name={availableBanks.length === 0 ? "+ Add bank" : "+ Add another bank"} icon={false} type={2} onClick={_handleAddMoreBank} />
                                                    </div>
                                                    <div className="col-auto col-sm-6 col-md-4 col-lg-4 flex-fill-res ">
                                                        <PrimaryButton name='Continue'
                                                            loading={loading}
                                                            disabled={loading}
                                                            inActive={availableBanks.length === 0 || availableBanks.some(bank => bank.is_default) === false ? true : false}
                                                            className={`w-100 padding-12px-tb padding-8px-tb-res padding-12px-lr-res e-font-16`}
                                                            onPress={_handleContinue} />
                                                    </div>
                                                </div>
                                            </>
                                    }
                                </>
                        }

                    </div>
                </div>


                {/* find ifsc modal  */}
                {ifscModal === true &&
                    <FindIFSCModal selectedBank={selectedBank}
                        selectedBranch={selectedBranch}
                        bankSearchModal={_handleBankSearchModal}
                        branchSearchModal={_handleBranchSearchModal}
                        onModalClose={() => {
                            setIfscModal(false);
                            setSelectedBank({});
                            setSelectedBranch({});
                        }}
                        handleFind={_handleFind} />
                }

                {/* bank search modal  */}
                {bankSearchModal === true &&
                    <SearchBankModal data={bankList}
                        selectedBank={selectedBank}
                        bankLoading={bankDataLoading}
                        searchValue={bankDataSearch}
                        handleClearSearch={() => setBankDataSearch("")}
                        handleSearch={(value) => setBankDataSearch(value)}
                        handleSelectedBank={_handleSelectBank}
                        onModalClose={() => {
                            setBankSearchModal(false);
                            setBankDataSearch("");
                        }} />
                }

                {/* branch search modal  */}
                {branchSearchModal === true &&
                    <SearchBranchModal data={branchList}
                        selectedBranch={selectedBranch}
                        branchLoading={branchDataLoading}
                        searchValue={branchSearch}
                        handleClearSearch={() => setBranchSearch("")}
                        handleSearch={(value) => setBranchSearch(value)}
                        handleSelectedBranch={_handleSelectBranch}
                        onModalClose={() => {
                            setBranchSearchModal(false);
                            setBranchSearch("");
                        }} />
                }

                {
                    showAddBankProofModal &&
                    <VerifyBankModal
                        buttonName="Continue"
                        onSuccess={_handleBankProofAddSuccess}
                        onClose={() => setShowAddBankProofModal(false)} />
                }

            </div>

            {showVerifyLoader && <VerifyLoader />}
        </>
    )
}

export default AddBank;